import { Injectable } from '@angular/core';
import {
    Production,
    ProductionSmallCard,
    ProductionSmallCardMilestoneAvg,
} from '../interfaces/production';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    FundedLoanTypeGroupDistributionModel,
    LoanCategoryProductionFundedDistModel,
    ProductionFundedDistModel,
} from '../interfaces/production-funded-dist';
import { IncentivesCharts } from 'app/modules/shared/classes/incentives-chart-options';
import { ILabelCollection, Incentives } from 'app/modules/incentives/interfaces/incentives-data';
import { ICamparisonInfo, ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';
import { LimeGearCharts } from 'app/modules/shared/classes/chart-data';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import * as Highcharts from 'highcharts';
import { IChartTitleData } from 'app/modules/shared/interfaces/card-data';

@Injectable({
    providedIn: 'root',
})
export class ProductionService {
    constructor(private _http: HttpClient, private reusableFunctions: ReusableFunctions) {}

    displayLargeProductionCardByPurpose(
        fromDatePeriod: string,
        priorCompareTo: string,
        milestoneKey: number,
        channel: string,
        loanType: string,
        loanPurpose: string,
        hFilterJSON: string
    ): Observable<Production[]> {
        return this._http.post<Production[]>(
            'https://' + window.location.host + '/api/v0.5/production/purpose/',
            {
                FromDatePeriod: fromDatePeriod,
                PriorCompareTo: priorCompareTo,
                MilestoneKey: milestoneKey,
                Channel: channel,
                LoanType: loanType,
                LoanPurpose: loanPurpose,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    displayLargeProductionCardByLoanType(
        fromDatePeriod: string,
        priorCompareTo: string,
        milestoneKey: number,
        channel: string,
        loanType: string,
        loanPurpose: string,
        hFilterJSON: string
    ): Observable<Production[]> {
        return this._http.post<Production[]>(
            'https://' + window.location.host + '/api/v0.5/production/loantype/',
            {
                FromDatePeriod: fromDatePeriod,
                PriorCompareTo: priorCompareTo,
                MilestoneKey: milestoneKey,
                Channel: channel,
                LoanType: loanType,
                LoanPurpose: loanPurpose,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    displayLargeProductionCardFallout(
        fromDatePeriod: string,
        priorCompareTo: string,
        channel: string,
        loanType: string,
        loanPurpose: string,
        hFilterJSON: string
    ): Observable<Production[]> {
        return this._http.post<Production[]>(
            'https://' + window.location.host + '/api/v0.5/production/fallout/',
            {
                FromDatePeriod: fromDatePeriod,
                PriorCompareTo: priorCompareTo,
                Channel: channel,
                LoanType: loanType,
                LoanPurpose: loanPurpose,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    displaySmallKPICardValue(
        fromDatePeriod: string,
        priorCompareTo: string,
        kpiMatrix: number,
        channel: string,
        loanType: string,
        loanPurpose: string,
        hFilterJSON: string
    ): Observable<ProductionSmallCard> {
        return this._http.post<ProductionSmallCard>(
            'https://' + window.location.host + '/api/v0.5/production/smallcard/',
            {
                FromDatePeriod: fromDatePeriod,
                PriorCompareTo: priorCompareTo,
                KPIMatrix: kpiMatrix,
                Channel: channel,
                LoanType: loanType,
                LoanPurpose: loanPurpose,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    displaySmallMilestoneAvgCardValue(
        fromDatePeriod: string,
        priorCompareTo: string,
        milestoneKey: number,
        avgType: string,
        channel: string,
        loanType: string,
        loanPurpose: string,
        hFilterJSON: string
    ): Observable<ProductionSmallCardMilestoneAvg> {
        return this._http.post<ProductionSmallCardMilestoneAvg>(
            'https://' + window.location.host + '/api/v0.5/production/smallcard-milestoneavg/',
            {
                FromDatePeriod: fromDatePeriod,
                PriorCompareTo: priorCompareTo,
                MilestoneKey: milestoneKey,
                AvgType: avgType,
                Channel: channel,
                LoanType: loanType,
                LoanPurpose: loanPurpose,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    displayFundedProduct(
        fromDatePeriod: string,
        channel: string,
        loanType: string,
        loanPurpose: string,
        hFilterJSON: string
    ): Observable<ProductionFundedDistModel[]> {
        return this._http.post<ProductionFundedDistModel[]>(
            'https://' + window.location.host + '/api/v0.5/production/insight/distribution',
            {
                FromDatePeriod: fromDatePeriod,
                Channel: channel,
                LoanType: loanType,
                LoanPurpose: loanPurpose,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    displayLoanCategoryFunedProduct(
        parameters
    ): Observable<LoanCategoryProductionFundedDistModel[]> {
        return this._http.post<LoanCategoryProductionFundedDistModel[]>(
            'https://' +
                window.location.host +
                '/api/v0.5/production/insight/loan-category-distribution',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    setChartData(
        stackData: any[],
        lineData: any[],
        property: string,
        alternateProperty: string,
        groupByClass: string,
        tootipTotalName: string,
        colors: object,
        dateRange: string,
        xLabelOffset: boolean = false,
        periodVerbiage: string = 'period'
    ) {
        let dataSet = stackData.map((obj) => ({ ...obj }));
        let stackColumnsData: IncentivesCharts.ChartdataSeries[] = [];
        let xAxisLabelList: ILabelCollection[] = [];
        let groupingNames = this.reusableFunctions.getUniqueItemList(dataSet, groupByClass);
        groupingNames.forEach((ItemClass) => {
            let classData = dataSet.filter((item) => item[groupByClass] === ItemClass);
            let dataInf = this.setPointData(
                classData,
                property,
                alternateProperty,
                tootipTotalName,
                dateRange,
                periodVerbiage
            );
            const itemClassName = ItemClass;
            stackColumnsData.push({
                name: itemClassName,
                data: dataInf.dataSet,
                color: colors[ItemClass],
                type: 'column',
            });
            dataInf?.xLabelCollection?.forEach((xLabelInf) => {
                let indx = xAxisLabelList.findIndex(
                    (labelInf) => labelInf.index === xLabelInf.index
                );
                if (indx < 0) {
                    xAxisLabelList.push({
                        index: xLabelInf.index,
                        description: xLabelInf.description,
                    });
                }
            });
        });

        let lineChartData = this.setLineChartData(
            lineData,
            property,
            alternateProperty,
            xAxisLabelList,
            dateRange,
            xLabelOffset,
            periodVerbiage
        );
        return { stackColumnsData: stackColumnsData, lineData: lineChartData };
    }

    setPointData(
        classData: Production[],
        property: string,
        alternateProperty: string,
        tootipTotalName: string,
        dateRange: string,
        periodVerbiage: string = 'period'
    ) {
        let purposeItems = [];
        let xLabelCollection: ILabelCollection[] = [];
        classData.forEach((item) => {
            let name = this.reusableFunctions.setXValues(item, dateRange, periodVerbiage);
            xLabelCollection.push({
                index: item[periodVerbiage],
                description: item.datePeriodDescription,
            });
            purposeItems.push({
                y: item[property],
                name: name,
                properties: {
                    datePeriodDescription: item.datePeriodDescription,
                    period: item[periodVerbiage],
                    units: property,
                    eCloseOptionKey: item.eCloseOptionKey,
                    tootipTotalName: tootipTotalName,
                    alternateY: item[alternateProperty],
                },
            });
        });
        return { dataSet: purposeItems, xLabelCollection: xLabelCollection };
    }

    setLineChartData(
        data: Production[],
        property: string,
        alternateProperty: string,
        xlabelsList: ILabelCollection[],
        dateRange: string,
        xLabelOffset: boolean = false,
        periodVerbiage: string = 'period'
    ) {
        data.forEach((item, i) => (item.period = i));
        let seriesData: IncentivesCharts.ChartDataPoint[] = [];
        if (data.length > 0) {
            let dataSet = data.map((obj) => ({ ...obj }));
            let groupingNames = this.reusableFunctions.getUniqueItemList(dataSet, periodVerbiage);
            groupingNames.forEach((period, index) => {
                let groupsData = dataSet.filter((item) => item[periodVerbiage] === period);
                if (groupsData.length > 0 && xlabelsList.length > index) {
                    let xLabel: number | string = groupsData[0].period;
                    if (this.reusableFunctions.getChartXLabel(dateRange) === 'Month') {
                        xLabel = xlabelsList[index]?.description?.slice(0, 3);
                    }
                    if (xLabelOffset) {
                        xLabel = Number(xLabel) + 1;
                    }
                    seriesData.push({
                        y: this.reusableFunctions.calculateSumByProperty(groupsData, property),
                        name: xLabel.toString(),
                        properties: {
                            yearMonthCalendar: groupsData[0].datePeriodDescription,
                            period: groupsData[0].period,
                            units: this.reusableFunctions.calculateSumByProperty(groupsData, 'nbr'),
                            alternateY: groupsData[0][alternateProperty],
                            type: property,
                            datePeriodDescription: groupsData[0].datePeriodDescription,
                        },
                    });
                }
            });
        }
        return seriesData;
    }

    setAllChartSeriesOptions(
        cardInfo: ICardInfo,
        lineData: IncentivesCharts.ChartDataPoint[],
        stackColumnData: IncentivesCharts.ChartdataSeries[],
        compareTo: string,
        cardDrilldownUrl: string,
        compareToFriendly: string,
        loanPurpose: string,
        loanType: string,
        limeGearCharts: LimeGearCharts.LimeGearChart
    ) {
        let seresData: IncentivesCharts.ChartdataSeries[] = [];
        if (lineData.length) {
            let lineSeries = limeGearCharts.setLineChartSeriesOptions(
                lineData,
                'spline',
                '#A6266E',
                'circle',
                false,
                compareTo,
                cardDrilldownUrl,
                this.reusableFunctions.formatTitle(cardInfo.name),
                compareToFriendly,
                loanPurpose,
                loanType
            );
            seresData = stackColumnData.concat([lineSeries as IncentivesCharts.ChartdataSeries]);
        } else {
            return stackColumnData;
        }
        return seresData;
    }

    updateChartData(
        cardInfo: ICardInfo,
        chartOptions: Highcharts.Options,
        hasData: boolean,
        isDataReady: boolean,
        action: NoDataActionType,
        comparison: ICamparisonInfo,
        mainValue: number,
        secondaryValue: number,
        showUnits,
        isComparisonInverse: boolean,
        loanPurpose: string,
        loanType: string,
        compareTo: string
    ) {
        let largeCardInteriorOptions = Object.assign({}, cardInfo.largeCardInteriorOptions);
        if (hasData) {
            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                chartOptions
            );
        }
        let queryParams = {
            purpose: loanPurpose,
            loanType: loanType,
            compareTo: compareTo,
            periodGroup: 'T',
        };

        if (cardInfo.urlSegment.search('fall-out') > -1) {
            queryParams['falloutType'] = 'All';
        }

        let chartTitleData;

        if (comparison) {
            chartTitleData = this.getChartTitleData(
                cardInfo.name,
                comparison.comparisonValue,
                comparison.percentage,
                comparison.isComparable,
                null,
                mainValue,
                secondaryValue,
                false,
                showUnits,
                isComparisonInverse
            );
        }

        largeCardInteriorOptions.chartTitleData = chartTitleData;
        largeCardInteriorOptions.queryParams = Object.assign({}, queryParams);
        largeCardInteriorOptions.hasData = hasData;
        largeCardInteriorOptions.isDataReady = isDataReady;
        largeCardInteriorOptions.searchTerm = '';
        largeCardInteriorOptions.action = action;
        largeCardInteriorOptions.showCardControlArea = true;
        return Object.assign({}, largeCardInteriorOptions);
    }

    getChartTitleData(
        title: string,
        compareValue: number,
        comparisonPercent: number,
        isComparable: boolean,
        subValue: string,
        mainValue: number,
        secondaryValue: number,
        isSubValueVisible: boolean,
        ismainValueUnit: boolean,
        isInverseCompareValue: boolean
    ) {
        let isMainValueUnitVisible = true;
        let mainValueToShow: string = '';
        let secondaryValueToShow: string = '';
        let comparison = this.reusableFunctions.getCaret(
            compareValue,
            isInverseCompareValue,
            comparisonPercent,
            isComparable
        );
        let comparisonValue = comparison.comparison;

        if (ismainValueUnit) {
            mainValueToShow = this.reusableFunctions.formatUnits(mainValue);
            comparisonValue = comparisonValue.replace('$', '');
            secondaryValueToShow = this.reusableFunctions.formatMoney(secondaryValue);
        } else {
            mainValueToShow = this.reusableFunctions.formatMoney(mainValue);
            comparisonValue;
            secondaryValueToShow = this.reusableFunctions.formatUnits(secondaryValue);
        }

        if (subValue === null) {
            isSubValueVisible = false;
        }

        let chartTitleData: IChartTitleData;
        if (ismainValueUnit) {
            chartTitleData = {
                showChartCustomTitle: true,
                chartTile: title,
                chartLabelColor: comparison.color,
                chartComparisonValue: comparisonValue,
                chartMainValue: secondaryValueToShow,
                chartSecondaryValue: mainValueToShow,
                isMainValueUnitVisible: isMainValueUnitVisible,
                chartSubItemValue: subValue,
                isChartSubValueVisible: isSubValueVisible,
                svgIconString: comparison.svgData,
            };
        } else {
            chartTitleData = {
                showChartCustomTitle: true,
                chartTile: title,
                chartLabelColor: comparison.color,
                chartComparisonValue: comparisonValue,
                chartMainValue: mainValueToShow,
                chartSecondaryValue: secondaryValueToShow,
                isMainValueUnitVisible: isMainValueUnitVisible,
                chartSubItemValue: subValue,
                isChartSubValueVisible: isSubValueVisible,
                svgIconString: comparison.svgData,
            };
        }
        return chartTitleData;
    }

    displayLoanTypeGroupDistribution(
        parameters
    ): Observable<FundedLoanTypeGroupDistributionModel[]> {
        return this._http.post<FundedLoanTypeGroupDistributionModel[]>(
            'https://' +
                window.location.host +
                '/api/v0.5/production/insight/loan-type-group-distribution',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }
}
